import {
  AloftPrice,
  AloftPriceJSON,
  isDefined,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { Input, ProductColor, theme } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';

import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { fieldTypographyProps } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';
import { getCompAdjustmentValue } from './utils.tsx';

export const AdjustmentField = ({
  columnIndex,
  field,
  control,
  getValues,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();
  const { manualAdjustmentKey } = field;

  if (!manualAdjustmentKey) {
    return (
      <Input
        type="text"
        value=""
        isDisabled
        onChange={() => {}}
        onClear={() => {}}
        __dangerDoNotUseSx={fieldTypographyProps.__dangerDoNotUseSx}
      />
    );
  }

  const fieldName =
    `columns.${columnIndex}.propertyData.editableData.manualAdjustments.${manualAdjustmentKey}` as FieldPath<PropertyGridForm>;

  const manualAdjustmentOverride = useWatch({ control, name: fieldName }) as
    | AloftPriceJSON
    | undefined;
  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });

  const compAdjustments = useAppSelector(selectCompAdjustments)[propertyId];
  let adjustmentValue = getCompAdjustmentValue(manualAdjustmentKey, compAdjustments);

  if (
    (manualAdjustmentKey === 'functionalUtility' ||
      manualAdjustmentKey === 'externalObsolescence') &&
    adjustmentValue.unitAmount > 0
  ) {
    adjustmentValue = adjustmentValue.mul(-1);
  }

  // Local state to track raw input value while typing
  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedNewValue = orUndefined(AloftPrice.fromDollarLike)(newValue);
    if (parsedNewValue?.equals(adjustmentValue)) return;

    const parsedNewValueJSON = parsedNewValue?.toJSON();
    onChange(parsedNewValueJSON);
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const displayValue = (
          orUndefined(schAloftPrice.parse)(manualAdjustmentOverride) ?? adjustmentValue
        )?.toString(true);

        const handleFocus = () => {
          setIsFocused(true);
          // Remove $ symbol when focusing for easier editing (handles both "$100" and "-$100")
          const valueWithoutDollar =
            displayValue?.replace(/-?\$/, (match) => (match === '-$' ? '-' : '')) ?? '';
          setRawInputValue(valueWithoutDollar);
        };

        const handleBlur = () => {
          setIsFocused(false);

          const inputToParse = rawInputValue !== undefined ? rawInputValue : displayValue;

          // Handle empty or zero cases
          const trimmedInput = inputToParse.trim();

          try {
            if (
              !inputToParse ||
              inputToParse.trim() === '' ||
              trimmedInput === '-$0' ||
              trimmedInput === '-$' ||
              trimmedInput === '-' ||
              AloftPrice.fromDollarLike(inputToParse).equals(AloftPrice.zero)
            ) {
              handleSetFieldValue(onChange, '$0');
            } else {
              handleSetFieldValue(onChange, inputToParse);
            }
          } catch {
            handleSetFieldValue(onChange, displayValue);
          }

          setRawInputValue(undefined);
        };

        const handleChange = (v: string) => setRawInputValue(v.replace(/^0+/, ''));

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            setRawInputValue(undefined);
            setIsFocused(false);
            onChange(adjustmentValue?.toJSON() ?? undefined);
          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
            (e.target as HTMLInputElement).blur();
          }
        };

        const inputValue =
          isFocused && isDefined(rawInputValue) ? rawInputValue : (displayValue ?? '');

        return (
          <Input
            ref={ref}
            type="text"
            value={inputValue}
            __dangerDoNotUseSx={{
              height: '100%',
              padding: '4px 8px',
              border: manualAdjustmentOverride
                ? `1px dashed ${theme.colors.buttonDefault}`
                : '1px solid transparent',
              outline: 'none',
              borderRadius: '0',
              fontSize: '14px',
              textAlign: 'right',
              backgroundColor: theme.colors.textOffset,
              color: adjustmentValue?.getDisplayColor() as ProductColor | undefined,
              _focusVisible: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
                background: theme.colors.surface3,
              },
              _hover: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
              },
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onClear={() => {
              setRawInputValue('');
              onChange(AloftPrice.zero.toJSON());
            }}
            onKeyDown={handleKeyDown}
          />
        );
      }}
    />
  );
};
